import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Loader, MultiSelect, Select, Tooltip } from '@mantine/core';
import { RefreshCw } from 'lucide-react';
import type { ProviderConfigField } from '@bmai/shared';
import type { ProviderOption } from './IntegrationsService';

interface IntegrationOptionFieldProps {
    field: ProviderConfigField;
    value: unknown;
    multi: boolean;
    onChange: (value: unknown) => void;
    onLoadOptions: (route: string) => Promise<ProviderOption[]>;
}

function staticOptions(field: ProviderConfigField): ProviderOption[] | null {
    const src = field.optionSource;
    if (src && 'options' in src) return src.options;
    return null;
}

function routeOf(field: ProviderConfigField): string | null {
    const src = field.optionSource;
    if (src && 'route' in src) return src.route;
    return null;
}

export default function IntegrationOptionField({
    field,
    value,
    multi,
    onChange,
    onLoadOptions,
}: IntegrationOptionFieldProps): React.ReactElement {
    const initialStatic = staticOptions(field);
    const route = routeOf(field);

    const [options, setOptions] = useState<ProviderOption[]>(initialStatic ?? []);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const reload = useCallback(async (): Promise<void> => {
        if (!route) return;
        setLoading(true);
        setError(null);
        try {
            const opts = await onLoadOptions(route);
            setOptions(opts);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [route, onLoadOptions]);

    useEffect(() => {
        if (route) void reload();
    }, [route, reload]);

    const rightSection = route ? (
        loading ? (
            <Loader size="xs" />
        ) : (
            <Tooltip label="Reload options">
                <ActionIcon variant="subtle" size="sm" onClick={() => void reload()} aria-label="Reload options">
                    <RefreshCw size={14} />
                </ActionIcon>
            </Tooltip>
        )
    ) : undefined;

    const description = field.description;

    if (multi) {
        const arr = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
        return (
            <MultiSelect
                label={field.displayLabel}
                description={description}
                error={error ?? undefined}
                required={field.required}
                data={options}
                value={arr}
                onChange={(v) => onChange(v)}
                searchable
                clearable
                rightSection={rightSection}
                onDropdownOpen={() => { if (route) void reload(); }}
            />
        );
    }

    return (
        <Select
            label={field.displayLabel}
            description={description}
            error={error ?? undefined}
            required={field.required}
            data={options}
            value={typeof value === 'string' ? value : null}
            onChange={(v) => onChange(v ?? '')}
            searchable
            clearable
            rightSection={rightSection}
            onDropdownOpen={() => { if (route) void reload(); }}
        />
    );
}
